import { components } from "@certn/api/generated-schemas/client-portal";
import { useClientPortalAPI } from "@certn/api/src/useClientPortalAPI";
import { BasicButton } from "@certn/cascadia";
import { Popover, Text } from "@certn/cascadia-react";
import { PopoverActions } from "@certn/cascadia-react/components/popover/PopoverActions";
import { PopoverContent } from "@certn/cascadia-react/components/popover/PopoverContent";
import { formatDate } from "@certn/utils";
import { useContext } from "react";
import { OverlayTriggerStateContext } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { PopoverDetails } from "../types";
interface Props {
  status: components["schemas"]["ClientPortalOverallStatus"];
  decision: components["schemas"]["ClientPortalDecision"];
  caseId: string;
  data?: PopoverDetails;
  /**
   * Row-level "created" timestamp from the case list payload. Used to render
   * "Date ordered" in the DRAFT (Quickscreen in-progress) popover so the list
   * is informative without opening the case. Optional to keep existing callers
   * compatible.
   */
  created?: string;
  /** Hide case-details/report/activity footers; DRAFT "Continue application" (Quickscreen) stays. */
  isCaseNavigationHidden?: boolean;
}

export function StatusPopover({
  data,
  status,
  decision,
  caseId,
  created,
  isCaseNavigationHidden = false,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const overlayTriggerState = useContext(OverlayTriggerStateContext);
  const isOpen = overlayTriggerState?.isOpen;

  let popoverDetails: PopoverDetails = {};

  const { data: details } = useClientPortalAPI(
    isOpen && !data
      ? `/api/internal/client-portal/case/${caseId}/status-popover-details/`
      : null
  );

  if (!data) {
    popoverDetails = (details?.popover_details as PopoverDetails) || {};
  } else {
    popoverDetails = data;
  }

  const displayValues = [
    {
      title: t("Email delivered"),
      value: formatDate(popoverDetails?.email_delivered_at, "PP", ""),
    },
    {
      title: t("Email opened"),
      value: Object.keys(popoverDetails).includes("email_opened")
        ? popoverDetails?.email_opened
          ? formatDate(popoverDetails?.email_opened, "PP", "")
          : t("No")
        : undefined,
    },
    {
      title: t("Application opened"),
      value: Object.keys(popoverDetails).includes("application_opened")
        ? popoverDetails?.application_opened
          ? formatDate(popoverDetails?.application_opened, "PP", "")
          : t("No")
        : undefined,
    },
    {
      title: t("Applicant submitted"),
      value: formatDate(popoverDetails?.applicant_submitted_at, "PP", ""),
    },
    {
      title: t("Applicant last active"),
      value: formatDate(popoverDetails?.applicant_last_active, "PP", ""),
    },
    {
      title: t("Applicant declined"),
      value: formatDate(popoverDetails?.applicant_declined_at, "PP", ""),
    },
    {
      title: t("Certn last updated"),
      value: formatDate(popoverDetails?.certn_last_updated, "PP", ""),
    },
    {
      title: t("Checks completed"),
      value:
        popoverDetails?.num_checks_ordered &&
        popoverDetails?.num_checks_completed &&
        `${popoverDetails?.num_checks_ordered} of ${popoverDetails?.num_checks_completed}`,
    },
    {
      title: t("Case completed"),
      value: formatDate(popoverDetails?.case_completed_at, "PP", ""),
    },
    {
      title: t("Case expiry"),
      value: formatDate(popoverDetails?.order_expiry, "PP", ""),
    },
    {
      title: t("Dispute initiated on"),
      value: formatDate(popoverDetails?.dispute_initiated_at, "PP", ""),
    },
    {
      title: t("Cancelled by"),
      value: popoverDetails?.cancelled_by,
    },
    {
      title: t("Cancelled on"),
      value: formatDate(popoverDetails?.cancelled_on, "PP", ""),
    },
    {
      title: t("Decision made on"),
      value: formatDate(popoverDetails?.case_decision_made_at, "PP", ""),
    },
  ];
  const viewDetailsButton = (
    <BasicButton
      size="small"
      onPress={() => {
        navigate(`/case-details/${caseId}`, {
          state: { last: { href: `${location.pathname}${location.search}` } },
        });
      }}
      testID="status-popover-view-details-button"
    >
      {t("View details")}
    </BasicButton>
  );
  const viewReportButton = (
    <BasicButton
      size="small"
      onPress={() => {
        navigate(`/case-details/${caseId}`, {
          state: { last: { href: `${location.pathname}${location.search}` } },
        });
      }}
      testID="status-popover-view-report-button"
    >
      {t("View report")}
    </BasicButton>
  );
  const viewActivityButton = (
    <BasicButton
      size="small"
      onPress={() => {
        navigate(`/case-details/${caseId}/activity-log`, {
          state: { last: { href: `${location.pathname}${location.search}` } },
        });
      }}
      testID="status-popover-view-activity-button"
    >
      {t("View activity")}
    </BasicButton>
  );
  const continueApplicationButton = (
    <BasicButton
      size="small"
      onPress={() => {
        navigate(`/quickscreen/${caseId}`, {
          state: {
            last: { href: `${location.pathname}${location.search}` },
          },
        });
      }}
      testID="continue-application-button"
    >
      {t("Continue application")}
    </BasicButton>
  );

  const isDraft = status.name === "DRAFT";
  const draftDisplayValues = [
    {
      title: t("Date ordered"),
      value: formatDate(created, "PP", ""),
    },
    {
      title: t("Certn last updated"),
      value: formatDate(popoverDetails?.certn_last_updated, "PP", ""),
    },
  ];

  const showCaseNavigationActions = !isCaseNavigationHidden;

  return (
    <Popover placement="top">
      <PopoverContent testID="status-popover">
        <>
          {isDraft ? (
            <Text style="global/body-secondary" color="text-weak">
              {t(
                "A Quickscreen order is in progress. Complete the remaining details to submit the order."
              )}
            </Text>
          ) : (
            popoverDetails?.translated_long_status_description && (
              <Text style="global/body-secondary" color="text-weak">
                {popoverDetails?.translated_long_status_description}
              </Text>
            )
          )}
          {(isDraft ? draftDisplayValues : displayValues).map(
            ({ title, value }) => {
              if (value) {
                return (
                  <div key={title}>
                    <Text style="global/heading-small">{title}:</Text>{" "}
                    <Text>{value}</Text>
                  </div>
                );
              }
            }
          )}
        </>
      </PopoverContent>
      {isDraft && <PopoverActions>{continueApplicationButton}</PopoverActions>}
      {showCaseNavigationActions && (
        <>
          {["CLIENT_ACTION_REQUIRED"].includes(status.name) && (
            <PopoverActions>{viewDetailsButton}</PopoverActions>
          )}
          {["COMPLETE"].includes(status.name) && !decision && (
            <PopoverActions>{viewReportButton}</PopoverActions>
          )}
          {[
            "APPLICANT_STARTED",
            "APPLICANT_ACTION_REQUIRED",
            "IN_DISPUTE",
            "CANCELLED",
          ].includes(status.name) && (
            <PopoverActions>{viewActivityButton}</PopoverActions>
          )}
          {["CASE_IN_PROGRESS"].includes(status.name) && (
            <PopoverActions>
              {viewReportButton}
              {viewActivityButton}
            </PopoverActions>
          )}
        </>
      )}
    </Popover>
  );
}
